import type { Pedido } from "../../../../contexts/OrderContext";
import type { CreatedQuotationOrderData } from "../components/summary/QuotationSummary.types";

type PedidoWithQuotationStatus = Pedido & {
	ost_id?: number;
	status_orcamento?: {
		id: number;
		descricao: string;
	};
};

interface BuildRealPedidoFromQuotationParams {
	data: CreatedQuotationOrderData;
	productRules: unknown;
	selectedQualityName?: string | null;
	now?: Date;
}

function formatDateAsYmd(date: Date): string {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");

	return `${year}-${month}-${day}`;
}

function getNextBusinessDay(startDate: Date): Date {
	const nextDate = new Date(startDate);

	while (nextDate.getDay() === 0 || nextDate.getDay() === 6) {
		nextDate.setDate(nextDate.getDate() + 1);
	}

	return nextDate;
}

function getNextMonday(startDate: Date): Date {
	const nextDate = new Date(startDate);

	do {
		nextDate.setDate(nextDate.getDate() + 1);
	} while (nextDate.getDay() !== 1);

	return nextDate;
}

export function mapClosingDeliveryToOrderDetailsValue(
	deliveryOption?: string | null,
): string {
	switch (deliveryOption) {
		case "amanha":
		case "fds":
			return "Amanh\u00e3";
		case "urgente":
			return "URGENTE";
		case "hoje":
			return "Hoje";
		default:
			return "Hoje";
	}
}

export function buildClosingDeliveryDate(
	deliveryOption?: string | null,
	now = new Date(),
): string | null {
	switch (deliveryOption) {
		case "hoje":
		case "urgente":
			return formatDateAsYmd(getNextBusinessDay(now));
		case "amanha": {
			const tomorrow = new Date(now);
			tomorrow.setDate(tomorrow.getDate() + 1);
			return formatDateAsYmd(getNextBusinessDay(tomorrow));
		}
		case "fds":
			return formatDateAsYmd(getNextMonday(now));
		default:
			return null;
	}
}

export function buildRealPedidoFromQuotation({
	data,
	productRules,
	selectedQualityName,
	now = new Date(),
}: BuildRealPedidoFromQuotationParams): PedidoWithQuotationStatus {
	const isMetros = data.unitId === 3;
	const quantityNormalized = Number.isFinite(data.quantity) ? data.quantity : 0;
	const orderObservation =
		data.observacaoPedido?.trim() || "Orcamento registrado via cotacao CorelDRAW";
	const itemObservation = data.observacaoItem?.trim() || "";
	const deliveryOption = data.entrega || "normal";

	return {
		orc_id: data.orcId,
		ost_id: 1,
		observacao: orderObservation,
		nome_arquivo: null,
		valor_total: data.totalValue.toFixed(2),
		data_entrega: buildClosingDeliveryDate(deliveryOption, now),
		data_cadastro: null,
		data_emissao: null,
		valor_frete: "0.00",
		forma_pagamento_id: 11,
		valor_desconto: "0.00",
		cliente: {
			cli_id: data.clientId,
			nome: data.clientName,
			razao_social: data.clientName,
			nome_fantasia: data.clientName,
		},
		status: {
			id_pagamento: 1,
			orc_status_id: 1,
			descricao: "Aguardando pagamento",
			descricao_pagamento: "Aguardando pagamento",
		},
		status_orcamento: {
			id: 1,
			descricao: "Or\u00e7amento",
		},
		itens: [
			{
				oit_id: data.oitId,
				quantidade: quantityNormalized,
				observacao: itemObservation,
				estagio: "orcamento",
				valor_unitario: data.unitPrice.toFixed(2),
				valor_total: data.totalValue.toFixed(2),
				status: {
					oit_status_id: 1,
					descricao: "Aguardando",
				},
				qualidade: data.qualityName || selectedQualityName || "standard",
				contracao: "0px",
				entrega: deliveryOption,
				entrega_opcao: mapClosingDeliveryToOrderDetailsValue(deliveryOption),
				produto: {
					pro_id: data.productId,
					nome: data.productName,
					tipo: 1,
					categoria: data.categoriaItem,
					quantidade: quantityNormalized,
					unidade: {
						umd_id: data.unitId,
						descricao: isMetros ? "Metros" : "Unidade",
						sigla: isMetros ? "m" : "un",
					},
				},
				material: {
					mat_id: 0,
					nome: data.qualityName || selectedQualityName || "Padrao",
				},
				regras: productRules,
			},
		],
	} as PedidoWithQuotationStatus;
}
